import React, { useEffect, useMemo, useState } from 'react';
import Head from 'next/head';
import Link from 'next/link';
import Layout from '../../components/layout';
import Container from '../../components/container';
import FeaturedHeroPost from '../../components/featured-hero-post';
import FeaturedNewsPost from '../../components/featured-news-post';
import NavBar from '../../components/navbar';
import Icon from '../../components/icon/Icon';
import RoundsTable from '../../components/rounds-table';
import Countdown from '../../components/countdown';
import config from '../../lib/config';
import LoadMoreNews from '../../components/load-more-posts';
import { GetStaticProps } from 'next';
import { fetchAPI, fetchData, getPrimaryMenuItems } from '../../lib/api';
import { IMatchRoundData } from '../../models/matchRoundData';
import { H2, H4 } from '../../components/text';
import { IconButton } from '../../components/icon-button';
import { ISponsor } from '../../models/sponsor';
import { IPost } from '../../models/post';
import { IMenuItem } from '../../models/menu-item';

interface IProps {
	sponsors: ISponsor[];
	categoryName: string;
	posts: IPost[];
	primaryMenuItems: IMenuItem[];
	matchRoundsData: IMatchRoundData;
	preview?: boolean;
}

export default function NewsPage({
	posts,
	preview,
	categoryName,
	primaryMenuItems,
	sponsors,
	matchRoundsData,
}: IProps) {
	const [roundData, setRoundData] = useState<IMatchRoundData | null>(null);
	const [currentRound, setCurrentRound] = useState(matchRoundsData?.currentRound);

	const newsPosts = useMemo(() => {
		const [firstPost, secondPost, thirdPost, ...restPosts] = posts;
		return {
			highlight: {
				firstPost,
				restPosts: [secondPost, thirdPost],
			},
			restPosts,
		};
	}, [posts]);

	useEffect(() => {
		const fetchRoundData = async () => {
			try {
				const response = await fetch(
					`${config.apiUrl}/wp-json/statsperform/v1/round-schedule?round=${currentRound}`,
				);
				const data = await response.json();
				setRoundData(data);
			} catch (error) {
				console.error('Error fetching round data:', error);
			}
		};
		fetchRoundData();
	}, [currentRound]);

	const pageTitle = `${categoryName} | ${config.siteName}`;
	const meta = `Blijf op de hoogte van het laatste nieuws over ${categoryName}`;

	return (
		<Layout preview={preview} sponsors={sponsors} metaTitle={pageTitle} metaDesc={meta}>
			<Head>
				<title>{pageTitle}</title>
			</Head>
			<NavBar items={primaryMenuItems} />
			<Container className="py-12 xl:py-16">
				<div className="mb-12">
					<H2 className="py-6">Laatste nieuws</H2>
					<div className="grid grid-cols-12 lg:grid-rows-2 gap-6">
						{newsPosts.highlight.firstPost && (
							<div className="row-span-2 col-span-12 lg:col-span-8">
								<FeaturedHeroPost
									title={newsPosts.highlight.firstPost.node.title}
									coverImage={newsPosts.highlight.firstPost.node.featuredImage}
									slug={newsPosts.highlight.firstPost.node.slug}
									categoryName={categoryName}
								/>
							</div>
						)}

						{newsPosts.highlight.restPosts.map((post) => (
							<div className="col-span-12 md:col-span-6 lg:col-span-4 mb-0" key={post.node.slug}>
								<FeaturedNewsPost
									key={post.node.slug}
									title={post.node.title}
									coverImage={post.node.featuredImage}
									slug={post.node.slug}
									excerpt={post.node.excerpt}
								/>
							</div>
						))}
					</div>
				</div>

				<div className="flex flex-col lg:flex-row">
					<div className="w-full lg:w-4/12 xl:w-3/12 mb-12 h-full ">
						<div className="bg-black-500">
							<Countdown matchRoundsData={matchRoundsData} />
							{roundData && (
								<div className="w-full h-full relative pb-4">
									<div className="swiper-title pb-4 flex flex-row justify-center items-center">
										<IconButton
											icon="arrow-left"
											className="mr-2 bg-transparent"
											onClick={() => setCurrentRound(roundData.prevRound)}
											disabled={!roundData.prevRound}
										/>
										<H4 className="text-white mx-1 lg:mx-3 text-base lg:text-lg xl:text-xl text-center uppercase">
											Speelronde {roundData.currentRound}
										</H4>
										<IconButton
											icon="arrow-right"
											className="ml-2 bg-transparent"
											onClick={() => setCurrentRound(roundData.nextRound)}
											disabled={!roundData.nextRound}
										/>
									</div>
									<RoundsTable matchesData={roundData} snug />

									<div className="my-8 flex justify-center items-center">
										<Link href={'/programma'} className="flex items-center">
											<span className="inline-block mr-3 text-red-500 hover:text-red-700 font-inter font-semibold uppercase tracking-widest text-base lg:text-lg">
												Alle wedstrijden
											</span>
											<Icon icon={'arrow-right'} color="#E2013D" size={18} />
										</Link>
									</div>
								</div>
							)}
						</div>
					</div>
					<div className="flex-1 lg:pl-6 xl:pl-16 w-full lg:w-8/12 xl:w-9/12">
						<LoadMoreNews />
					</div>
				</div>
			</Container>
		</Layout>
	);
}

export const getStaticProps: GetStaticProps = async ({ preview = false }) => {
	const primaryMenuItems = await getPrimaryMenuItems();
	// Todo DEVP-144: Check for better solution then hardcoding the category id
	const categoryId = '1';
	const data = await fetchAPI<{ category: any; sponsors: any }>(
		`
		query CategoryQuery($categoryId: ID!) {
			category(id: $categoryId, idType: DATABASE_ID) {
				name
				posts (first: 3) {
					edges {
						node {
							id
							title
							slug
							date
							excerpt
							featuredImage {
								node {
									sourceUrl
								}
							}
							tags(first: 1) {
								nodes {
									name
								}
							}
							author {
								node {
									name
									firstName
									lastName
									avatar {
										url
									}
								}
							}
						}
					}
				}
			}
			sponsors(first: 20) {
				nodes {
					title
					sponsor_fields {
						subtitle
						websiteUrl
						mainSponsor
						invertedImage {
							sourceUrl
						}
					}
				}
			}
		}
	`,
		{ variables: { categoryId } },
	);

	let matchRoundsData: IMatchRoundData | null = null;

	try {
		matchRoundsData = await fetchRoundMatchData();
	} catch (error) {
		console.error('Error fetching data:', error);
	}

	const { sponsors, category } = data;

	return {
		props: {
			posts: category.posts.edges,
			categoryName: category.name,
			sponsors: sponsors.nodes,
			preview,
			primaryMenuItems,
			matchRoundsData,
		},
		revalidate: 300,
	};
};

const fetchRoundMatchData = async () => {
	const matchRoundsData = `${config.apiUrl}/wp-json/statsperform/v1/current-round`;
	return fetchData(matchRoundsData);
};
